import './DataGrid.css';
import Tristate from '@scanex/tristate';
import EventTarget from '@scanex/event-target';

const ENUM_ID = typeof Symbol === 'function' ? Symbol('enumeration id') : 1e+6;
const ENUM_LEN = 32;

const create_index = (items, indexBy) => {
    return items    
        .reduce((a,item) => {
            if (indexBy) {
                if (Array.isArray(indexBy) && indexBy.length > 0 && indexBy.every(k => item.hasOwnProperty(k))) {
                    let values = indexBy.map(k => item[k]);
                    const idx = get_hash (values);
                    if (idx) {
                        a[idx] = item;
                    }                
                }
                else if (typeof indexBy === 'string' && item.hasOwnProperty(indexBy)) {
                    const idx = item[indexBy];
                    if (idx) {
                        a[idx] = item;
                    }
                }
            }          
            else {
                item[ENUM_ID] = (0 | Math.random() * 9e+6).toString(36);
                a[ENUM_ID] = item;
            }  
            return a;
        },{});
};

const get_hash = values => {
    if (Array.isArray (values) && values.length > 0) {
        return btoa (values.join(''));
    }
    else if (typeof values === 'string') {
        return btoa(values);
    }
    else {
        return null;
    }
};

const rnd_str = L => {
    var s = '';
    var rnd_chr = () => {
      var n = Math.floor(Math.random() * 62);
      if (n < 10) return n; //1-10
      if (n < 36) return String.fromCharCode(n + 55); //A-Z
      return String.fromCharCode(n + 61); //a-z
    }
    while (s.length < L) s += rnd_chr();
    return s;
};

const serialize = obj => {
    return Object.keys(obj).map(k => obj[k]);
};

const sort = (items, field, asc) => {
    if (field) {
        return items
        .map((e, i) => { return {i: i, v: e}; })
        .sort((a, b) => {
            var left = a.v[field], right = b.v[field];

            if(left == null && right != null){
                return asc ? -1 : 1;
            }

            if(left != null && right == null){
                return asc ? 1 : -1;
            }

            if(typeof left == 'string') {
                left = left.toLowerCase();
            }

            if(typeof right == 'string') {
                right = right.toLowerCase();
            }

            if(left < right){
                return asc ? -1 : 1;
            }
            else if(left > right){
                return asc ? 1 : -1;
            }
            else if(left == right){
                var i = a.index, k = b.index;
                if(i < k){
                    return asc ? -1 : 1;
                }
                else if(i > k){
                    return asc ? 1 : -1;
                }
                else {
                    return 0;
                }
            }
        })
        .map(e => e.v);
    }
    else {
        return items;
    }
};

class DataGrid extends EventTarget {
    constructor(container, {customHeader = false, align = true, fields = {}, sortBy = {}, filter = null, adjustMode = 'auto', indexBy}){
        super();
        this._container = container;
        this._customHeader = customHeader;
        this._isCustomHeader = !!customHeader;
        this._container.classList.add('table-list');
        this._fields = fields;
        this._stats = {};        
        this._align = align;
        this._sortBy = sortBy;
        this._filter = filter;
        this._filtered = false;
        this._adjustMode = adjustMode;
        this._indexBy = indexBy;
        this._index = {};

        this._header = document.createElement('div');
        this._header.className = 'table-list-header';
        if (this._isCustomHeader) {
            this._header.style.display = 'none';
        }
        this._container.appendChild(this._header);

        this._body = document.createElement('div');
        this._body.className = 'table-list-body';
        this._container.appendChild(this._body);
        this._stopPropagation = this._stopPropagation.bind(this);        
    }
    _stopPropagation (e) {
        e.stopPropagation();
    }

    getItemByIndex(id) {
        return this._index[id];
    }
    
    set items (value) {
        if(Array.isArray(value)) {
            if (value.length > 0) {
                this._index = create_index (value, this._indexBy);
                if (this._sortBy.hasOwnProperty('field') && this._sortBy.hasOwnProperty('asc')) {
                    let i = -1;
                    let keys = Object.keys(this._fields);
                    for (let k = 0; k < keys.length; ++k){
                        if (this._sortBy.field === keys[k]) {
                            i = k;
                            break;
                        }
                    }
                    if (i >= 0) {
                        this._renderHeader();
                        this._reorder (i, this._sortBy.field, this._sortBy.asc);
                        this._attachColumnsEvents();
                    }
                }
                else {
                    this._render(this.items);
                }
            }            
            else {
                this._index = {};
                this._render([]);
            }
        }
        else {
            this._index = {};
            this._render(this.items);
        }
    }    
    get items () {
        return sort(serialize (this._index), this._sortBy.field, this._sortBy.asc);
    }
    refresh(){        
        if (this._sortBy.hasOwnProperty('field') && this._sortBy.hasOwnProperty('asc')) {
            let i = -1;
            let keys = Object.keys(this._fields);
            for (let k = 0; k < keys.length; ++k) {
                if (this._sortBy.field === keys[k]) {
                    i = k;
                    break;
                }
            }
            if (i >= 0) {
                this._renderHeader();
                this._reorder (i, this._sortBy.field, this._sortBy.asc);
                this._attachColumnsEvents();
            }
        }
        else {
            this._render(this.items);
        }        
        Object.keys(this._fields)
        .filter(k => typeof this._fields[k].tristate === 'object')
        .map(k => this._fields[k].tristate)
        .forEach(t => t.update());
    }
    _getCellAlign (type) {
        switch (type){
            case 'integer':
            case 'float':
                return 'right';
            case 'selector':
            case 'button':
            case 'boolean':
            case 'color':
                return 'center';
            default:
                return 'left';
        }
    }
    /*_renderCell (item, col) {
        const field = this._fields[col];
        const width = field.width;
        const align = this._align ? ` style="text-align: ${field.align || this._getCellAlign(field.type)}"` : '';        
        switch(field.type) {
            case 'selector':
                return Boolean(item[col]) ? `<td${align}><input type="checkbox" checked value="${col}" /></td>` : `<td${align}><input type="checkbox" value="${col}" /></td>`;
            case 'button':
                return `<td${align}><i class="table-list-button ${field.button}" /></td>`;
            case 'boolean':
                let val = typeof field.formatter === 'function' ? field.formatter (item) : item[col];
                const cell = (field.yes || field.no) ? `<i class="table-list-button ${field.icon} ${val ? (field.yes || '') : (field.no || '')}"></i>` : `${val ? '+' : ''}`;
                return `<td${align}>${cell}</td>`;
            case 'color':
                return `<td${align}>
                        <div class="table-list-color" style="${ typeof item[col] !== 'undefined' ? `border-color: ${item[col]}` : 'border: none'} ">&nbsp;</div>
                    </td>`;
            default:                
                if (typeof field.styler === 'function') {                    
                    return `<td${align}><i class="${field.styler(item)}"></i></td>`;
                }
                else {
                    let val = typeof field.formatter === 'function' ? field.formatter (item) : item[col];
                    return `<td${align}><span>${val}</span>${field.edit ? '<i class="cell-edit"></i>' :''}</td>`;
                }
                
        }
    }*/

    _renderCell (item, col) {
        const field = this._fields[col];
        const width = field.width;
        let padding = '';
        if (col === 'platform') {
            padding = 'padding-right:0;'
        }
        else if (col === 'stereo') {
            padding = 'padding-right:0;padding-left:0;';
        }
        const fldWidth = col === 'stereo' ? 'width:10px;' : '';
        const align = this._align ? ` style="text-align: ${field.align || this._getCellAlign(field.type)};${padding}${fldWidth}"` : '';  
        switch(field.type) {
            case 'selector':
                return Boolean(item[col]) ? `<td${align}><input type="checkbox" checked value="${col}" /></td>` : `<td${align}><input type="checkbox" value="${col}" /></td>`;
            case 'button':
                return `<td${align}><i class="table-list-button ${field.button}" /></td>`;
            case 'boolean':
                let val = typeof field.formatter === 'function' ? field.formatter (item) : item[col];
                const cell = (field.yes || field.no) ? `<i class="table-list-button ${field.icon} ${val ? (field.yes || '') : (field.no || '')}"></i>` : `${val ? '+' : ''}`;
                return `<td${align}>${cell}</td>`;
            case 'color':
                return `<td${align}>
                        <div class="table-list-color" style="${ typeof item[col] !== 'undefined' ? `border-color: ${item[col]}` : 'border: none'} ">&nbsp;</div>
                    </td>`;
            default:                
                if (typeof field.styler === 'function') {                    
                    return `<td${align}><i class="${field.styler(item)}"></i></td>`;
                }
                else {
                    let val = typeof field.formatter === 'function' ? field.formatter (item) : item[col];
                    return `<td${align}><span>${val}</span>${field.edit ? '<i class="cell-edit"></i>' :''}</td>`;
                }
                
        }
    }

    _attachEvents() {        
        const rows = this._body.querySelectorAll('tr');        
        for (let i = 0; i < rows.length; ++i){
            const row = rows[i];            
            let item = this._index[row.getAttribute('data-item-id')];
            this._attachRowEvents (row, item);            
        }
    }
    _attachRowEvents (row, item) {
        row.addEventListener('mouseover', this._handleRowMouseOver.bind(this, row, item));
        row.addEventListener('mouseout', this._handleRowMouseOut.bind(this, row, item));
        const cells = row.querySelectorAll('td');
        const fields = Object.keys(this._fields);
        for (let j = 0; j < cells.length; ++j){
            const name = fields[j];
            const field = this._fields[name];  
            let cell = cells[j];
            if (typeof field.edit === 'string') {  
                const val = cell.querySelector('span');
                val.addEventListener('click', this._handleCellClick.bind(this, row, val, name, field, item));
                let btn = cell.querySelector('i');
                btn.addEventListener('click', this._handleCellEdit.bind(this, row, cell, val, btn, name, field, item));
            }
            else {
                cell.addEventListener('click', this._handleCellClick.bind(this, row, cell, name, field, item));
            }
        }
    }
    _renderRow (item) {
        if (typeof this._filter !== 'function' || !this._filtered || this._filter (item)) {
            let idx = null;
            if (Array.isArray (this._indexBy) && this._indexBy.length > 0 && this._indexBy.every(k => item.hasOwnProperty(k))) {
                let values = this._indexBy.map(k => item[k]);
                idx = get_hash (values);
            }
            else if (typeof this._indexBy === 'string' && item.hasOwnProperty(this._indexBy)) {
                idx = item[this._indexBy];
            }
            else {
                idx = item[ENUM_ID];
            }
            return `<tr data-item-id="${idx}">${Object.keys(this._fields).map(this._renderCell.bind(this, item)).join('')}</tr>`;
        }
    }
    _render (items) {
        this._renderHeader();
        this._renderBody(items);
        this.adjustHeader();
        this._updateSelector();
        this._attachColumnsEvents();        
    }
    _renderBody(items) {
        this._clearEvents();
        if(items.length > 0) {
            this._body.innerHTML =
                `<table>
                    <colgroup>${Object.keys(this._fields).map(x => '<col />').join('')}</colgroup>
                    ${items.map(this._renderRow.bind(this)).join('')}
                </table>`;
            this._attachEvents();
        }
        else {
            this._body.innerHTML = '';
        }        
    }    
    _renderHeaderColumn(col) {
        const field = this._fields[col];
        let el = '';
        switch(field.type) {
            case 'selector':
                el = `<input class="table-list-tristate" type="checkbox" />`;
                break;
            case 'boolean':
            case 'string':
                if (typeof field.name === 'string') {
                    el = `<span>${field.name}</span>`;
                }
                else if (typeof field.columnIcon === 'string') {
                    el = `<i class="${field.columnIcon}"></i>`;
                }
                break;
            case 'button':
                if (typeof field.columnIcon === 'string') {
                    el = `<i class="${field.columnIcon}"></i>`;
                }
                else if (typeof field.name === 'string') {
                    el = `<span>${field.name}</span>`;
                }
                break;
            default:
                if (typeof field.name === 'string') {
                    el = `<span>${field.name}</span>`;
                }
                break;
        }
        return `<td${field.tooltip ? ` title="${field.tooltip}"` : ''} class="table-list-col" data-field="${col}">
            ${el}
            <i class="table-list-sort"${field.sortable ? '' : ' style="display: none"'}></i>
        </td>`;
    }
    get hasItems () {
        return Object.keys (this._index).length > 0;
    }
    _attachColumnsEvents() {
        if(this.hasItems) {
            const cols = this._header.querySelectorAll('td');
            const names = Object.keys(this._fields);
            for (let i = 0; i < cols.length; ++i){
                const name = names[i];                
                let field = this._fields[name];
                let col = cols[i];
                if(field.sortable) {
                    col.addEventListener('click', this._handleSort.bind(this, i));
                }
                if (field.type === 'selector') {
                    let ts = col.querySelector('.table-list-tristate');
                    ts.addEventListener('click', this._stopPropagation);
                    let items = this._body.querySelectorAll(`td:nth-child(${i + 1}) input[type="checkbox"]`);
                    field.tristate = new Tristate(ts, items);
                }
                col.addEventListener('click', e => {
                    let event = document.createEvent('Event');
                    event.initEvent('column:click', false, false);
                    event.detail = {col, field, name};
                    this.dispatchEvent(event);
                });
            }
        }        
    }
    _updateColumns(k, asc){
        const buttons = this._header.querySelectorAll('.table-list-sort');
        for (let i = 0; i < buttons.length; ++i){
            let btn = buttons[i];
            if(i === k) {
                if(asc) {
                    btn.classList.remove ('table-list-sort-down');
                    btn.classList.add ('table-list-sort-up');
                }
                else {
                    btn.classList.remove ('table-list-sort-up');
                    btn.classList.add ('table-list-sort-down');
                }
            }
            else {
                btn.classList.remove ('table-list-sort-up');
                btn.classList.remove ('table-list-sort-down');
            }
        }
    }
    _updateSelector(){
        const cols = this._header.querySelectorAll('td');
        const fields = serialize(this._fields);
        for (let i = 0; i < cols.length; ++i){
            let field = fields[i];            
            if (field.tristate) {
                let items = this._body.querySelectorAll(`td:nth-child(${i + 1}) input[type="checkbox"]`);
                field.tristate.state = items;
                break;
            }
        }
    }   
    _reorder(i, name, asc) {
        this._updateColumns(i, asc);
        this._renderBody (sort(this.items, name, asc));
        this.adjustHeader();
        this._updateSelector();
    }
    _handleSort (k) {              
        this._sortBy.asc = !this._sortBy.asc;
        this._sortBy.field =  Object.keys(this._fields)[k];
        this._reorder(k, this._sortBy.field, this._sortBy.asc);

        let event = document.createEvent('Event');
        event.initEvent('sort', false, false);
        event.detail = {field: this._fields[this._sortBy.field], name: this._sortBy.field, asc: this._sortBy.asc };
        this.dispatchEvent(event);
    }
    
    _renderHeader () {
        this._header.innerHTML = 
        `<table>
            <colgroup>${Object.keys(this._fields).map(x => '<col />').join('')}</colgroup>
            <tr>${Object.keys(this._fields).map(this._renderHeaderColumn.bind(this)).join('')}</tr>
        </table>`;        
    }
    adjustHeader() {
        if (this.hasItems) {
            const fields = serialize(this._fields);
            let widths = [];
            let header;
            if (this._isCustomHeader === false) {
                header = this._header;
            }
            else {
                header = this._customHeader;
            }
            const hc = header.querySelector('colgroup').children;
            const bc = this._body.querySelector('colgroup').children;
            if (this._adjustMode === 'auto') {
                const row = this._body.querySelector('tr');
                if (row && row.children) {
                    const cells = row.children;
                    const cols = header.querySelectorAll('td');                    
                    let total = 0;
                    for (let i = 0; i < cells.length; ++i){
                        let c = cells[i].getBoundingClientRect();
                        let h = cols[i].getBoundingClientRect();            
                        widths.push (Math.max(c.width, h.width));
                        total += widths[i];
                    }
                    for (let i = 0; i < hc.length; ++i){
                        const w = `${widths[i]}px`;
                        hc[i].style.width = w;
                        bc[i].style.width = w;
                    }
                }                
            }
            else {
                for (let i = 0; i < hc.length; ++i){
                    const w = `${fields[i].width}px`;
                    hc[i].style.width = w;
                    bc[i].style.width = w;
                }                 
            }                                                
        }           
    }
    _clearEvents () {        
        const rows = this._container.querySelectorAll('tr');
        for (let i = 0; i < rows.length; ++i){
            let row = rows[i];
            row.removeEventListener('mouseover', this._handleRowMouseOver);
            row.removeEventListener('mouseout', this._handleRowMouseOut);
            this._clearRowEvents(row);
        }
    }
    _clearRowEvents (row) {
        const cells = row.querySelectorAll('td');
        for (let i = 0; i < cells.length; ++i){
            cells[i].removeEventListener('click', this._handleCellClick);
        }
    }    
    _handleCellClick (row, cell, name, field, item, e) {

        let event = document.createEvent('Event');
        event.initEvent('cell:click', false, false);
        event.detail = { row, cell, name, field, item };
        this.dispatchEvent(event);

        e.stopPropagation();
    }
    _handleRowMouseOver(row, item, e) {        
        let cells = row.querySelectorAll('td');
        Object.keys(this._fields)
        .map(k => this._fields[k])        
        .forEach ((x, j) => {
            if (typeof x.edit === 'string') {
                let cell = cells[j];
                if (!cell.querySelector('input')) {
                    cell.querySelector('i').classList.add(x.edit);
                }             
            }
        });
        let event = document.createEvent('Event');
        event.initEvent('row:mouseover', false, false);
        event.detail = {row,item};
        this.dispatchEvent(event);

        e.stopPropagation();      
    }
    _handleRowMouseOut(row, item, e) { 
        let cells = row.querySelectorAll('td');
        Object.keys(this._fields)
        .map(k => this._fields[k])        
        .forEach ((x, j) => {
            if (typeof x.edit === 'string') {
                cells[j].querySelector('i').classList.remove(x.edit);
            }
        });

        let event = document.createEvent('Event');
        event.initEvent('row:mouseout', false, false);
        event.detail = {row,item};
        this.dispatchEvent(event);

        e.stopPropagation();        
    }
    _handleCellEdit(row, cell, val, btn, name, field, item) {
        let input = document.createElement('input');
        input.className = 'cell-edit-input';
        input.type = 'text';
        input.value = cell.innerText;
        input.style.width = `100%`;
        val.style.display = 'none';
        btn.classList.remove (field.edit);               
        let detach = () => {        
            document.body.removeEventListener('click', change);
            input.removeEventListener('keydown', handler);
            // input.removeEventListener('blur', change);
            input.removeEventListener('focus', this._stopPropagation);
            input.removeEventListener('click', this._stopPropagation);
            cell.removeChild (input);

            this.adjustHeader();

            let event = document.createEvent('Event');
            event.initEvent('cell:edit', false, false);
            event.detail =  { row, cell, name, field, item };
            this.dispatchEvent(event);
        };
        let change = () => {
            val.innerText = input.value;
            item[name] = val.innerText;
            val.style.display = 'inline-block';
            detach();
        };
        let revert = () => {
            val.innerText = item[name];
            val.style.display = 'inline-block';
            detach();
        };        
        let handler = e => {
            switch (e.keyCode) {
                case 13:
                    change();
                    break;
                case 27:
                    revert();
                    break;
                default:
                    break;
            }
        };
        document.body.addEventListener('click', change);
        input.addEventListener('focus', this._stopPropagation);
        input.addEventListener ('keydown', handler);
        input.addEventListener('click', this._stopPropagation);
        cell.insertBefore (input, val);
        input.focus();
        input.select();
        // input.addEventListener('blur', change);
    }    
    getRow (id) {
        let items = this._body.querySelectorAll('[data-item-id]');
        for (let i = 0; i < items.length; ++i) {
            let item = items[i];
            if (item.getAttribute('data-item-id') === id.toString()) {
                return item;
            }
        }
        return null;
    }
    getCol (name) {
        let items = this._header.querySelectorAll('[data-field]');
        for (let i = 0; i < items.length; ++i) {
            let item = items[i];
            if (item.getAttribute('data-field') === name) {
                return item;
            }
        }
        return null;
    }
    scrollToRow(id){
        let rows = this._body.querySelectorAll('[data-item-id]');
        for (let i = 0; i < rows.length; ++i) {
            let row = rows[i];
            if (row.getAttribute('data-item-id') === id.toString()) {
                let { height } = row.getBoundingClientRect();
                this._body.scrollTop = i * height;
                break;
            }            
        }                
    }    
    get filtered () {
        return this._filtered;
    }
    set filtered (value) {
        this._filtered = value;
        this.refresh();
    }
    get filteredItems () {
        if (typeof this._filter === 'function' && this.filtered) {
            return this.items.filter(item => this._filter(item));
        }
        else {
            return this.items;
        }
    }
    set filter (value) {
        this._filter = value;
    }
    get count () {
        let rows = this._body.querySelectorAll('[data-item-id]');
        if (rows && rows.length) {
            return rows.length;
        }
        else {
            return 0;
        };
    }
    redrawRow (id, item) {
        let row = this.getRow(id);        
        this._index[id] = item;
        if (row) {
            this._clearRowEvents(row);
            row.insertAdjacentHTML('afterend', this._renderRow (item));
            row.parentElement.removeChild(row);
            this._attachRowEvents(this.getRow(id), item);
            this._updateSelector();
        }
    }

    sort (data) {              
        this._sortBy.asc = data.asc;
        this._sortBy.field =  data.field;
        this._reorder(data.field, this._sortBy.field, this._sortBy.asc);

        let event = document.createEvent('Event');
        event.initEvent('sort', false, false);
        event.detail = {field: this._fields[this._sortBy.field], name: this._sortBy.field, asc: this._sortBy.asc };
        this.dispatchEvent(event);
    }
}

export { DataGrid, ENUM_ID, get_hash };